/**
 * Makes React resilient to in-place DOM mutation by browser page-translation
 * tools (Google Translate / Chrome auto-translate, and similar extensions).
 *
 * Those tools replace text nodes with `<font>` wrappers, orphaning the original
 * text node that React's fiber still references. On the next commit, React calls
 * `parent.insertBefore(newNode, orphanedNode)` or `parent.removeChild(orphanedNode)`
 * and the browser throws `NotFoundError: ... is not a child of this node`, tearing
 * down the whole subtree via the error boundary.
 *
 * We guard the two native methods so a parent/child mismatch degrades gracefully
 * (append instead of insert-before; skip an already-detached remove) rather than
 * throwing. This keeps translation working instead of disabling it.
 *
 * See https://github.com/facebook/react/issues/11538
 */
export function installDomTranslationGuard(): void {
  // No-op during SSR — there is no DOM to translate.
  if (typeof window === "undefined" || typeof Node === "undefined") return;

  const proto = Node.prototype as Node & {
    __translationGuardInstalled?: boolean;
  };
  if (proto.__translationGuardInstalled) return;
  proto.__translationGuardInstalled = true;

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was moved/detached by a translation tool. Appending
      // preserves React's intent (place the node) without throwing.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Already detached by a translation tool — nothing to remove.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };
}
